import { GraphQL } from '@guestgoodie/graphql'
import * as Popover from '@radix-ui/react-popover'
import { HugeiconsIcon } from '@hugeicons/react'
import { Copy02Icon, Delete01Icon } from '@hugeicons/core-solid-rounded'
import { useLingui } from '@lingui/react/macro'

import { RecurrenceInput } from './RecurrenceInput'
import { popoverContainer } from '@/utils/popovers'
import { RecurrenceLabel } from './RecurrenceLabel'

interface Props {
  recurrence: GraphQL.RecurrenceFragmentFragment
  hasDuration?: boolean
  readOnly?: boolean
  onChange: (rrule: GraphQL.RecurrenceFragmentFragment) => void
  onDuplicate?: () => void
  onDelete?: () => void
}

export default function RecurrenceSelect(props: Props) {
  const {
    hasDuration = false,
    recurrence,
    readOnly = false,
    onChange,
    onDuplicate,
    onDelete
  } = props

  const { t } = useLingui()

  return (
    <Popover.Root>
      <div className="relative flex w-auto flex-col">
        <Popover.Trigger asChild>
          <button
            className="rounded-small border border-text-light bg-white text-regular text-text placeholder:text-text-light focus-within:outline-solid focus-within:outline-primary p-4"
            type="button"
            tabIndex={0}
            disabled={readOnly}
          >
            <RecurrenceLabel recurrence={recurrence} />
          </button>
        </Popover.Trigger>
        <div className="flex flex-row justify-end gap-2 px-2">
          {onDuplicate && (
            <button
              className="size-6 p-1 hover:bg-black/30 rounded-full"
              type="button"
              tabIndex={0}
              onClick={onDuplicate}
              aria-label={t`Duplicate`}
            >
              <HugeiconsIcon icon={Copy02Icon} className="size-full" />
            </button>
          )}
          {onDelete && (
            <button
              className="size-6 p-1 hover:bg-black/30 rounded-full"
              type="button"
              tabIndex={0}
              onClick={onDelete}
              aria-label={t`Remove`}
            >
              <HugeiconsIcon icon={Delete01Icon} className="size-full" />
            </button>
          )}
        </div>
      </div>
      <Popover.Portal container={popoverContainer}>
        <Popover.Content
          align="start"
          className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 rounded bg-white p-6 shadow-md outline-hidden z-popover"
        >
          <RecurrenceInput
            value={recurrence}
            onChange={onChange}
            showUntil={hasDuration}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
